import React from 'react';
import { FiX } from 'react-icons/fi';
import { Button } from './Button';
import { Modal } from './Modal';

interface GfciInformationModalProps {
  open: boolean;
  onClose: () => void;
}

export const GfciInformationModal: React.FC<GfciInformationModalProps> = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white bg-opacity-95 flex items-center justify-between px-6 py-4 border-b border-gray-200 rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">GFCI Outlet Information</h2>
          <Button
            type="button"
            variant="text"
            className="text-gray-700 text-2xl font-bold bg-white bg-opacity-80 rounded-full w-8 h-8 flex items-center justify-center"
            aria-label="Close modal"
            onClick={onClose}
          >
            <FiX className="w-6 h-6" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">What is a GFCI Outlet?</h3>
            <p className="text-sm text-gray-700 mb-4">
              A GFCI (Ground Fault Circuit Interrupter) outlet is a special type of electrical outlet that protects against electrical shock. 
              It monitors the flow of electricity and quickly shuts off power if it detects an imbalance that could indicate a dangerous situation.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">How to Identify a GFCI Outlet</h3>
            <p className="text-sm text-gray-700 mb-4">
              GFCI outlets are easily recognizable by their distinctive "TEST" and "RESET" buttons located between the two plug receptacles. 
              These buttons allow you to test the outlet's safety function and reset it if it has been tripped.
            </p>
            <div className="flex justify-center mb-4">
              <img 
                src="/images/gfci-outlet.webp" 
                alt="GFCI outlet showing TEST and RESET buttons"
                className="max-w-full h-auto rounded-lg shadow-md"
                style={{ maxHeight: '300px' }}
              />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Why is it Important for Bidets?</h3>
            <p className="text-sm text-gray-700 mb-4">
              Since bidets that require electricity operate near water sources, manufacturers strongly recommend using GFCI outlets for safety. 
              The GFCI protection helps prevent electrical shock in case of water contact with electrical components.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Installation Considerations</h3>
            <p className="text-sm text-gray-700 mb-4">
              If you don't currently have a GFCI outlet near your toilet, you may need to have one installed by a qualified electrician. 
              This is an important safety measure that protects you, your family, your guests, your clients, and your patrons.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              For more detailed information, you can visit{' '}
              <Button
                variant="text"
                className="text-blue-500 underline text-xs"
                onClick={() => window.open('https://home.howstuffworks.com/question117.htm', '_blank')}
              >
                HowStuffWorks
              </Button>{' '}
              or consult with a licensed electrician.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
